<template>
  <div class="verify-screen">
    <!-- 顶部装饰条 -->
    <div class="top-decoration"></div>

    <!-- 主体内容 -->
    <div class="main-content">
      <!-- 标题与输入区域 -->
      <div class="header">
        <div class="title-wrapper">
          <div class="title-icon">
            <i class="fas fa-qrcode"></i>
          </div>
          <div class="title-text">
            <h1 class="title-main">取餐码核销</h1>
            <p class="title-sub">Verification</p>
          </div>
        </div>

        <div class="input-wrapper">
          <div class="input-with-suggestions">
            <div class="input-shell">
              <a-auto-complete
                ref="autoCompleteRef"
                v-model:value="inputPickCode"
                class="pickcode-auto-complete"
                :options="autoCompleteOptions"
                placeholder="输入取餐码回车后核销"
                @select="handleAutoSelect"
                @keyup.enter="handleVerifyInput"
              />
              <button
                v-if="inputPickCode"
                class="clear-btn"
                type="button"
                @mousedown.prevent
                @click="handleClearInput"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
          <button class="verify-btn" @click="handleVerifyInput">
            <i class="fas fa-check-circle mr-2"></i>核销
          </button>
          <button class="scan-open-btn" @click="openScanModal">
            <i class="fas fa-qrcode mr-2"></i>二维码核销
          </button>
        </div>
      </div>

      <!-- 核销结果提示 -->
      <div v-if="successMessage" class="success-toast">
        <i class="fas fa-check-circle mr-2"></i>{{ successMessage }}
      </div>

      <!-- 取餐码列表 -->
      <div class="codes-container">
        <div class="codes-header">
          <div class="codes-title">
            <span class="label">待核销取餐码</span>
            <span class="badge">{{ allPickCodeList.length }}</span>
          </div>
          <div v-if="lastRefreshTime" class="last-refresh">
            <i class="fas fa-sync-alt mr-1"></i> 最近更新：{{ lastRefreshTime }}
          </div>
        </div>

        <div class="codes-list">
          <div class="codes-list-inner">
            <div class="codes-list-transition">
              <a-popover
                v-for="item in allPickCodeList"
                :key="item.pickCode"
                placement="top"
                trigger="hover"
              >
                <template #content>
                  <div class="order-popover-content">
                    <div class="popover-item">
                      <span class="popover-label">订单号：</span>
                      <span class="popover-value">{{ item.order?.orderNo || '-' }}</span>
                    </div>
                    <div class="popover-item">
                      <span class="popover-label">取餐码：</span>
                      <span class="popover-value">{{ item.pickCode }}</span>
                    </div>
                    <div v-if="item.order?.shopName" class="popover-item">
                      <span class="popover-label">餐厅：</span>
                      <span class="popover-value">{{ item.order.shopName }}</span>
                    </div>
                    <div v-if="item.order?.customerName" class="popover-item">
                      <span class="popover-label">客户：</span>
                      <span class="popover-value">{{ item.order.customerName }}</span>
                    </div>
                    <div v-if="item.order?.totalAmount" class="popover-item">
                      <span class="popover-label">金额：</span>
                      <span class="popover-value">¥{{ item.order.totalAmount.toFixed(2) }}</span>
                    </div>
                    <div v-if="item.order?.createTime" class="popover-item">
                      <span class="popover-label">下单时间：</span>
                      <span class="popover-value">{{ formatDateTime(item.order.createTime) }}</span>
                    </div>
                    <!-- 菜品明细 -->
                    <div
                      v-if="item.order?.foodList && item.order.foodList.length > 0"
                      class="popover-food-list"
                    >
                      <div class="popover-divider"></div>
                      <div class="popover-section-title">菜品明细</div>
                      <div class="popover-food-list-scroll">
                        <div
                          v-for="(food, foodIndex) in item.order.foodList"
                          :key="foodIndex"
                          class="popover-food-item"
                        >
                          <div class="food-name-row">
                            <span class="food-name">{{
                              food.shopFoodName || food.foodName || '-'
                            }}</span>
                            <span class="food-quantity">x{{ food.quantity || 1 }}</span>
                          </div>
                          <div v-if="food.foodSpecName" class="food-spec">
                            规格：{{ food.foodSpecName }}
                          </div>
                          <div v-if="food.foodCookDesc" class="food-cook">
                            做法：{{ food.foodCookDesc }}
                          </div>
                          <div v-if="food.remark" class="food-remark">
                            备注：{{ food.remark }}
                          </div>
                          <div class="food-price-row">
                            <span class="food-price"
                              >¥{{ (food.amount || food.price || 0).toFixed(2) }}</span
                            >
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
                <div
                  class="code-item"
                  :class="{ 'vip-code': isVipCode(item.pickCode) }"
                  @click="handleVerifyClick(item)"
                >
                  <div class="code-glow"></div>
                  <div class="code-content">
                    <i v-if="isVipCode(item.pickCode)" class="fas fa-crown vip-icon"></i>
                    <span class="code-text">{{ item.pickCode }}</span>
                  </div>
                </div>
              </a-popover>
            </div>
          </div>

          <div v-if="allPickCodeList.length === 0" class="empty-state">
            <i class="fas fa-inbox empty-icon"></i>
            <p class="empty-text">暂无待核销的取餐码</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 二维码扫码模态框 -->
    <div v-if="showScanModal" class="scan-modal-backdrop">
      <div class="scan-modal">
        <div class="scan-modal-header">
          <h2 class="scan-modal-title">二维码核销</h2>
          <button class="scan-close-btn" type="button" @click="closeScanModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="scan-modal-status">
          <span>状态：{{ isScannerRunning ? '扫描中' : '摄像头未启动' }}</span>
          <span>待处理：{{ scanQueue.length }}</span>
        </div>
        <div class="scan-feedback">
          <p class="scan-feedback-line"> 最近识别：{{ lastScannedText || '暂无' }} </p>
          <p class="scan-feedback-line result"> 核销结果：{{ scanVerifyResult || '等待扫码' }} </p>
        </div>
        <div class="scan-panel">
          <div :id="scannerContainerId" class="scan-view"></div>
          <div v-if="!isScannerRunning" class="scan-empty">
            <span>无法启动摄像头，请检查 HTTPS、摄像头权限或设备可用性</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部装饰条 -->
    <div class="bottom-decoration"></div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue';
  import { Html5Qrcode } from 'html5-qrcode';
  import { getOrderList, completeOrder } from '@/service/order';
  import { fetchShopList } from '@/service/base';
  import { readSelectedShop } from '@/utils/selectedShop';
  import type { ShopDetailModel } from '@/model/shop';
  import { OrderListItemModel } from '@/model/order';
  import { message } from 'ant-design-vue';
  import okSoundSrc from '@/assets/audio/ok.mp3';
  import errorSoundSrc from '@/assets/audio/error.mp3';

  interface PickCodeItem {
    pickCode: string;
    order?: OrderListItemModel;
  }

  // 输入框取餐码（兼做筛选关键字）
  const inputPickCode = ref('');
  const autoCompleteRef = ref<{ focus?: () => void } | null>(null);

  // 完整取餐码列表（后续可由接口填充，下方列表始终展示全部）
  const allPickCodeList = ref<PickCodeItem[]>([]);

  // AutoComplete 选项：根据当前输入内容进行筛选
  const autoCompleteOptions = computed(() => {
    const keyword = inputPickCode.value.trim();
    const source = allPickCodeList.value;
    if (!keyword) {
      return source.map((item) => ({ value: item.pickCode }));
    }
    return source
      .filter((item) => item.pickCode.toLowerCase().includes(keyword.toLowerCase()))
      .map((item) => ({ value: item.pickCode }));
  });

  // 最近刷新时间（预留，后续可在拉取接口时更新）
  const lastRefreshTime = ref('');

  // 模拟核销结果提示
  const successMessage = ref('');

  // 店铺选择相关
  const shopList = ref<ShopDetailModel[]>([]);
  // 当前生效的店铺ID（仅来自 URL 或下拉选择）
  const currentShopId = ref<number | null>(null);

  // 自动刷新定时器
  let autoRefreshTimer: NodeJS.Timeout | null = null;

  // 扫码核销相关
  const showScanModal = ref(false);
  const scannerContainerId = 'pickcode-qrcode-reader';
  const isScannerRunning = ref(false);
  const scanQueue = ref<string[]>([]);
  const isQueueProcessing = ref(false);
  const lastScannedText = ref('');
  const scanVerifyResult = ref('');
  let html5Qrcode: Html5Qrcode | null = null;
  const lastScanTimeMap = new Map<string, number>();
  let okAudio: HTMLAudioElement | null = null;
  let errorAudio: HTMLAudioElement | null = null;

  // 是否 VIP 取餐码（示例：以 V 开头）
  const isVipCode = (code: string | undefined) => {
    if (!code) return false;
    return code.toUpperCase().startsWith('V');
  };

  // 加载当前账号可见的餐厅，用于解析缓存餐厅名称。
  const loadShopList = async () => {
    try {
      const response = await fetchShopList({
        pageNum: 1,
        pageSize: 100,
        data: { shopStatus: 0 },
      });
      if (response.code === 200 && response.result && response.result.list) {
        shopList.value = response.result.list;
      }
    } catch (error) {
      console.error('加载店铺列表失败:', error);
    }
  };

  // 更新刷新时间
  const updateRefreshTime = () => {
    const now = new Date();
    lastRefreshTime.value = `${String(now.getHours()).padStart(2, '0')}:${String(
      now.getMinutes(),
    ).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
  };

  // 获取取餐码列表
  const fetchPickCodeList = async () => {
    const shopId = currentShopId.value;
    if (!shopId) {
      console.error('未获取到 shopId，无法加载取餐码列表');
      return;
    }

    try {
      const respOrder = await getOrderList({
        pageNum: 1,
        pageSize: 999,
        data: {
          shopId: shopId,
          orderStatuss: [8],
          sortType: 2,
        },
      });

      if (respOrder.code === 200 && respOrder.result?.list) {
        type OrderWithPickCode = Partial<OrderListItemModel> & { pickCode?: string };
        // 过滤出有 pickCode 的订单，并保存完整订单信息
        const pickCodeItems: PickCodeItem[] = respOrder.result.list
          .filter((order: OrderWithPickCode) => order.pickCode && order.pickCode.trim() !== '')
          .map((order: OrderWithPickCode) => ({
            pickCode: order.pickCode,
            order: order as OrderListItemModel,
          }));

        // 调试：打印当前所有待核销取餐码
        try {
          const codesForLog = pickCodeItems.map((x) => x.pickCode);
          // eslint-disable-next-line no-console
          console.log(
            '[PickCodeVerification] 当前待核销取餐码数量:',
            codesForLog.length,
            '列表:',
            codesForLog.join(', '),
          );
        } catch (e) {
          // ignore log errors
        }

        // 按下单时间倒序排序，最新的取餐码排在最前面，避免新数据刷到列表底部看不到
        pickCodeItems.sort((a, b) => {
          const ta = a.order?.createTime ? new Date(a.order.createTime).getTime() : 0;
          const tb = b.order?.createTime ? new Date(b.order.createTime).getTime() : 0;
          return tb - ta;
        });

        console.log('pickCodeItems', pickCodeItems);
        allPickCodeList.value = pickCodeItems;
        updateRefreshTime();
      } else {
        console.error('获取订单列表失败:', respOrder.msg);
        allPickCodeList.value = [];
      }
    } catch (error) {
      console.error('获取取餐码列表失败:', error);
      allPickCodeList.value = [];
    }
  };

  // 启动自动刷新
  const startAutoRefresh = () => {
    // 每5秒刷新一次
    autoRefreshTimer = setInterval(() => {
      if (currentShopId.value) {
        fetchPickCodeList();
      }
    }, 10000);
  };

  // 停止自动刷新
  const stopAutoRefresh = () => {
    if (autoRefreshTimer) {
      clearInterval(autoRefreshTimer);
      autoRefreshTimer = null;
    }
  };

  // 初始化
  onMounted(async () => {
    initAudioPlayers();
    await loadShopList();
    const cachedShopId = readSelectedShop();
    if (cachedShopId) {
      currentShopId.value = cachedShopId;
      await fetchPickCodeList();
      // 启动自动刷新
      startAutoRefresh();
    }
  });

  // 清理
  onUnmounted(async () => {
    stopAutoRefresh();
    await stopScanner();
    okAudio?.pause();
    errorAudio?.pause();
  });

  const showResultToast = (text: string) => {
    successMessage.value = text;
    scanVerifyResult.value = text;
    setTimeout(() => {
      successMessage.value = '';
    }, 2000);
  };

  const initAudioPlayers = () => {
    if (!okAudio) {
      okAudio = new Audio(okSoundSrc);
      okAudio.preload = 'auto';
      okAudio.playsInline = true;
      okAudio.volume = 1;
      okAudio.load();
    }
    if (!errorAudio) {
      errorAudio = new Audio(errorSoundSrc);
      errorAudio.preload = 'auto';
      errorAudio.playsInline = true;
      errorAudio.volume = 1;
      errorAudio.load();
    }
  };

  const playSound = (audio: HTMLAudioElement | null, src: string) => {
    const target = audio ?? new Audio(src);
    target.playsInline = true;
    target.volume = 1;
    try {
      target.currentTime = 0;
      const playPromise = target.play();
      if (playPromise) {
        void playPromise.catch(() => {
          // 某些浏览器在高频播放时会拒绝，降级为克隆实例播放
          const clone = target.cloneNode(true) as HTMLAudioElement;
          clone.playsInline = true;
          clone.volume = 1;
          void clone.play().catch(() => {
            // ignore play errors
          });
        });
      }
    } catch (error) {
      // ignore play errors
    }
  };

  // 移动端（尤其 iOS）需要在用户手势中预先解锁音频播放
  const unlockAudio = () => {
    initAudioPlayers();
    try {
      [okAudio, errorAudio].forEach((audio) => {
        if (!audio) return;
        audio.muted = true;
        void audio.play().then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.muted = false;
        });
      });
    } catch (error) {
      // ignore unlock errors
    }
  };

  const playSuccessSound = () => {
    playSound(okAudio, okSoundSrc);
  };

  const playErrorSound = () => {
    playSound(errorAudio, errorSoundSrc);
  };

  // 从扫码结果中提取取餐码，优先读取 URL 参数中的 pickCode
  const normalizePickCode = (rawText: string): string => {
    const text = rawText.trim();
    if (!text) return '';
    try {
      const parsedUrl = new URL(text);
      const pickCodeFromQuery =
        parsedUrl.searchParams.get('pickCode') ||
        parsedUrl.searchParams.get('pickcode') ||
        parsedUrl.searchParams.get('pickupCode');
      if (pickCodeFromQuery?.trim()) {
        return decodeURIComponent(pickCodeFromQuery.trim());
      }
      return text;
    } catch {
      return text;
    }
  };

  const verifyByCode = async (code: string, orderIdFromItem?: number) => {
    let orderId = orderIdFromItem;
    if (!orderId) {
      const targetItem = allPickCodeList.value.find((item) => item.pickCode === code);
      orderId = targetItem?.order?.orderId;
    }

    if (!orderId) {
      showResultToast(`取餐码 ${code} 不存在`);
      message.warning(`取餐码 ${code} 不存在`);
      playErrorSound();
      return;
    }

    try {
      const resp = await completeOrder({ orderId: orderId });
      if (resp.code === 200) {
        allPickCodeList.value = allPickCodeList.value.filter((item) => item.pickCode !== code);
        showResultToast(`取餐码 ${code} 核销成功`);
        message.success(`取餐码 ${code} 核销成功`);
        playSuccessSound();
      } else {
        showResultToast(`核销失败：${resp.msg || '接口返回错误'}`);
        message.error(resp.msg || '核销失败');
        playErrorSound();
      }
    } catch (e) {
      console.error('核销异常:', e);
      showResultToast(`核销异常：${e instanceof Error ? e.message : '未知错误'}`);
      message.error('核销异常，请稍后重试');
      playErrorSound();
    }
  };

  const processScanQueue = async () => {
    if (isQueueProcessing.value) return;
    isQueueProcessing.value = true;
    try {
      while (scanQueue.value.length > 0) {
        const code = scanQueue.value.shift();
        if (!code) continue;
        await verifyByCode(code);
      }
    } finally {
      isQueueProcessing.value = false;
    }
  };

  const pushScanToQueue = (code: string) => {
    const now = Date.now();
    const previous = lastScanTimeMap.get(code) || 0;
    if (now - previous < 3000) return;
    lastScanTimeMap.set(code, now);
    scanQueue.value.push(code);
    void processScanQueue();
  };

  const handleScanSuccess = (decodedText: string) => {
    const code = normalizePickCode(decodedText);
    if (!code) return;
    lastScannedText.value = code;
    scanVerifyResult.value = `已识别 ${code}，正在核销...`;
    pushScanToQueue(code);
  };

  const isHttpsOrLocal = (): boolean => {
    const { protocol, hostname } = window.location;
    return protocol === 'https:' || hostname === 'localhost' || hostname === '127.0.0.1';
  };

  const startScanner = async (): Promise<boolean> => {
    if (isScannerRunning.value) return true;
    if (!isHttpsOrLocal()) {
      message.warning('扫码需要 HTTPS 环境（localhost 除外）');
      return false;
    }

    try {
      const cameras = await Html5Qrcode.getCameras();
      if (!cameras.length) {
        message.warning('未检测到可用摄像头');
        return false;
      }
      if (!html5Qrcode) {
        html5Qrcode = new Html5Qrcode(scannerContainerId);
      }

      const isMobile = window.innerWidth <= 768;
      const qrboxSize = isMobile ? 180 : 220;
      const startConfig = {
        fps: 12,
        qrbox: { width: qrboxSize, height: qrboxSize },
      };

      const startErrorCallback = () => {
        // 忽略单次识别失败，避免频繁提示影响扫码体验
      };

      try {
        await html5Qrcode.start(
          { facingMode: { exact: 'environment' } },
          startConfig,
          handleScanSuccess,
          startErrorCallback,
        );
      } catch {
        try {
          await html5Qrcode.start(
            { facingMode: 'environment' },
            startConfig,
            handleScanSuccess,
            startErrorCallback,
          );
        } catch {
          const backCamera =
            cameras.find((item) => /back|rear|environment|后置|后摄/gi.test(item.label)) ||
            cameras[cameras.length - 1] ||
            cameras[0];
          await html5Qrcode.start(
            backCamera.id,
            startConfig,
            handleScanSuccess,
            startErrorCallback,
          );
        }
      }

      isScannerRunning.value = true;
      return true;
    } catch (error) {
      console.error('启动扫码失败:', error);
      message.error('启动扫码失败，请检查摄像头权限');
      await stopScanner();
      return false;
    }
  };

  const stopScanner = async () => {
    scanQueue.value = [];
    lastScanTimeMap.clear();
    if (!html5Qrcode) {
      isScannerRunning.value = false;
      return;
    }
    try {
      if (html5Qrcode.isScanning) {
        await html5Qrcode.stop();
      }
      await html5Qrcode.clear();
    } catch (error) {
      console.error('关闭扫码失败:', error);
    } finally {
      isScannerRunning.value = false;
      html5Qrcode = null;
    }
  };

  const openScanModal = async () => {
    unlockAudio();
    showScanModal.value = true;
    scanVerifyResult.value = '扫码后将自动核销';
    await nextTick();
    await startScanner();
  };

  const closeScanModal = async () => {
    await stopScanner();
    showScanModal.value = false;
  };

  // 通过输入框触发核销
  const handleVerifyInput = async () => {
    unlockAudio();
    const code = inputPickCode.value.trim();
    if (!code) {
      return;
    }
    await verifyByCode(code);
    inputPickCode.value = '';
  };

  // 点击取餐码触发核销（此时已拿到完整订单信息）
  const handleVerifyClick = async (item: PickCodeItem) => {
    unlockAudio();
    const code = item.pickCode;
    const orderId = item.order?.orderId;
    if (!orderId) {
      showResultToast(`取餐码 ${code} 缺少订单ID`);
      return;
    }
    await verifyByCode(code, orderId);
  };

  // 选中 AutoComplete 选项（键盘上下 + 回车/鼠标），只填入，不自动核销，避免误操作
  const handleAutoSelect = (code: string) => {
    inputPickCode.value = code;
    // 保持/恢复焦点在输入框上，方便继续按回车进行核销
    setTimeout(() => {
      autoCompleteRef.value?.focus?.();
    }, 0);
  };

  const handleClearInput = () => {
    inputPickCode.value = '';
    autoCompleteRef.value?.focus?.();
  };

  // 格式化日期时间
  const formatDateTime = (dateTime: string | Date | undefined): string => {
    if (!dateTime) return '-';
    try {
      const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } catch (e) {
      return '-';
    }
  };
</script>

<style scoped>
  .verify-screen {
    width: 100vw;
    height: 100vh;
    background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    font-family: 'PingFang SC', 'Microsoft YaHei', 'Arial', sans-serif;
    position: relative;
    color: #ffffff;
  }

  .top-decoration,
  .bottom-decoration {
    height: 4px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(59, 130, 246, 0.4) 25%,
      rgba(37, 99, 235, 0.6) 50%,
      rgba(59, 130, 246, 0.4) 75%,
      transparent 100%
    );
    position: relative;
    z-index: 10;
    animation: shimmer 3s infinite;
  }

  .main-content {
    flex: 1;
    padding: 40px 60px;
    display: flex;
    flex-direction: column;
    position: relative;
    background: linear-gradient(135deg, #1a1d29 0%, #252936 50%, #1a1d29 100%);
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 40px;
  }

  .title-wrapper {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .title-icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(37, 99, 235, 0.15));
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    color: #60a5fa;
    box-shadow: 0 4px 20px rgba(59, 130, 246, 0.3);
    border: 1px solid rgba(59, 130, 246, 0.3);
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
  }

  .title-icon:hover {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(37, 99, 235, 0.25));
    border-color: rgba(59, 130, 246, 0.5);
    box-shadow: 0 6px 24px rgba(59, 130, 246, 0.4);
    transform: scale(1.05);
  }

  .title-icon:active {
    transform: scale(0.95);
  }

  .title-text {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .title-main {
    font-size: 40px;
    font-weight: 700;
    margin: 0;
    letter-spacing: 2px;
  }

  .title-sub {
    font-size: 18px;
    color: rgba(255, 255, 255, 0.7);
    letter-spacing: 3px;
    text-transform: uppercase;
    margin: 0;
  }

  .input-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 420px;
    flex-wrap: wrap;
  }

  .input-with-suggestions {
    flex: 1;
  }

  .input-shell {
    position: relative;
    width: 100%;
  }

  .pickcode-auto-complete {
    width: 100%;
  }

  /* 自定义 AutoComplete 样式，使其类似原来的圆角输入框 */
  :deep(.pickcode-auto-complete .ant-select-selector) {
    border-radius: 999px !important;
    border: 1px solid rgba(255, 255, 255, 0.25) !important;
    background: rgba(0, 0, 0, 0.3) !important;
    padding: 0 14px !important;
    height: 44px !important; /* 统一高度，便于垂直居中 */
    display: flex;
    align-items: center;
  }

  :deep(.pickcode-auto-complete .ant-select-selection-search-input) {
    color: #ffffff !important;
    font-size: 18px !important;
    height: 40px !important;
    line-height: 40px !important;
  }

  :deep(.pickcode-auto-complete .ant-select-selection-placeholder) {
    color: rgba(255, 255, 255, 0.4) !important;
    font-size: 18px !important;
  }

  :deep(.pickcode-auto-complete.ant-select-focused .ant-select-selector) {
    border-color: rgba(59, 130, 246, 0.8) !important;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3) !important;
  }

  /* 自定义清除按钮，位置在输入框右侧中间 */
  .clear-btn {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: none;
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 10px;
    padding: 0;
  }

  .clear-btn:hover {
    background: rgba(255, 255, 255, 0.4);
  }

  .success-toast {
    margin-bottom: 16px;
    align-self: flex-start;
    padding: 8px 16px;
    border-radius: 999px;
    background: rgba(34, 197, 94, 0.15);
    border: 1px solid rgba(34, 197, 94, 0.6);
    color: #bbf7d0;
    display: inline-flex;
    align-items: center;
    font-size: 16px;
  }

  .verify-btn {
    padding: 10px 24px;
    border-radius: 999px;
    border: none;
    background: linear-gradient(135deg, #f97316, #ea580c);
    color: #ffffff;
    font-size: 18px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 15px rgba(249, 115, 22, 0.4);
    transition: all 0.3s ease;
  }

  .verify-btn:hover {
    background: linear-gradient(135deg, #ea580c, #dc2626);
    box-shadow: 0 6px 20px rgba(249, 115, 22, 0.5);
    transform: translateY(-1px);
  }

  .scan-open-btn {
    padding: 10px 24px;
    border-radius: 999px;
    border: 1px solid rgba(59, 130, 246, 0.45);
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    color: #ffffff;
    font-size: 18px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 15px rgba(37, 99, 235, 0.35);
    transition: all 0.3s ease;
  }

  .scan-open-btn:hover {
    background: linear-gradient(135deg, #1d4ed8, #1e40af);
    box-shadow: 0 6px 20px rgba(37, 99, 235, 0.45);
    transform: translateY(-1px);
  }

  .codes-container {
    flex: 1;
    margin-top: 10px;
    display: flex;
    flex-direction: column;
  }

  .codes-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }

  .codes-title {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .codes-title .label {
    font-size: 22px;
    font-weight: 600;
  }

  .codes-title .badge {
    min-width: 40px;
    height: 32px;
    padding: 0 10px;
    border-radius: 999px;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.25), rgba(37, 99, 235, 0.2));
    border: 1px solid rgba(59, 130, 246, 0.3);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: 600;
    color: #60a5fa;
    box-shadow: 0 0 15px rgba(59, 130, 246, 0.2);
  }

  .last-refresh {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.7);
  }

  .codes-list {
    flex: 1;
    background: rgba(30, 33, 45, 0.8);
    border-radius: 24px;
    padding: 24px 28px;
    position: relative;
    overflow: hidden;
    border: 1px solid rgba(59, 130, 246, 0.2);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05);
  }

  .codes-list-inner {
    max-height: 100%;
    overflow-y: auto;
    position: relative;
    z-index: 1;
  }

  .codes-list-transition {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  }

  .code-item {
    position: relative;
    padding: 16px 22px;
    background: rgba(37, 42, 58, 0.9);
    border-radius: 14px;
    border: 1px solid rgba(59, 130, 246, 0.2);
    min-width: 130px;
    cursor: pointer;
    overflow: hidden;
    animation: slideIn 0.5s ease-out backwards;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05);
    transition: all 0.3s ease;
  }

  .code-item:hover {
    transform: translateY(-2px);
    background: rgba(45, 51, 70, 0.95);
    border-color: rgba(59, 130, 246, 0.4);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(59, 130, 246, 0.3);
  }

  .code-glow {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle,
      rgba(59, 130, 246, 0.15) 0%,
      rgba(37, 99, 235, 0.08) 50%,
      transparent 70%
    );
    opacity: 0;
    transition: opacity 0.4s;
    pointer-events: none;
  }

  .code-item:hover .code-glow {
    opacity: 1;
  }

  .code-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    position: relative;
    z-index: 1;
  }

  .code-text {
    font-family: 'Courier New', 'Consolas', monospace;
    font-size: 40px;
    font-weight: 700;
    letter-spacing: 3px;
    text-shadow: 0 0 18px rgba(255, 255, 255, 0.3), 2px 2px 6px rgba(0, 0, 0, 0.7);
  }

  .vip-code {
    background: linear-gradient(135deg, rgba(249, 115, 22, 0.2), rgba(234, 88, 12, 0.15));
    border-color: rgba(249, 115, 22, 0.4);
    box-shadow: 0 4px 20px rgba(249, 115, 22, 0.3), 0 0 15px rgba(249, 115, 22, 0.2);
  }

  .vip-code:hover {
    background: linear-gradient(135deg, rgba(249, 115, 22, 0.25), rgba(234, 88, 12, 0.2));
    border-color: rgba(249, 115, 22, 0.5);
    box-shadow: 0 8px 24px rgba(249, 115, 22, 0.4), 0 0 20px rgba(249, 115, 22, 0.3);
  }

  .vip-icon {
    color: #ffd700;
    font-size: 26px;
    filter: drop-shadow(0 0 8px rgba(255, 215, 0, 0.8));
  }

  .empty-state {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    color: rgba(255, 255, 255, 0.7);
  }

  .empty-icon {
    font-size: 70px;
    color: rgba(255, 255, 255, 0.3);
  }

  .empty-text {
    font-size: 22px;
    margin: 0;
  }

  /* 动画与滚动条 */
  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .codes-list-inner::-webkit-scrollbar {
    width: 10px;
  }

  .codes-list-inner::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 10px;
  }

  .codes-list-inner::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.2));
    border-radius: 10px;
  }

  /* 响应式 */
  @media (max-width: 1366px) {
    .main-content {
      padding: 30px 40px;
    }

    .title-main {
      font-size: 32px;
    }

    .title-sub {
      font-size: 16px;
    }

    .pickcode-input {
      font-size: 16px;
    }

    .verify-btn {
      font-size: 16px;
      padding: 8px 20px;
    }

    .code-text {
      font-size: 34px;
    }
  }

  @media (max-width: 768px) {
    .verify-screen {
      height: auto;
      min-height: 100vh;
      overflow: auto;
    }

    .main-content {
      padding: 16px 14px 20px;
    }

    .header {
      flex-direction: column;
      align-items: stretch;
      gap: 12px;
      margin-bottom: 16px;
    }

    .title-wrapper {
      gap: 10px;
    }

    .title-icon {
      width: 46px;
      height: 46px;
      border-radius: 12px;
      font-size: 22px;
    }

    .title-main {
      font-size: 30px;
      letter-spacing: 1px;
    }

    .title-sub {
      font-size: 14px;
      letter-spacing: 1px;
    }

    .input-wrapper {
      min-width: 0;
      width: 100%;
      flex-direction: column;
      align-items: stretch;
      gap: 10px;
    }

    .input-with-suggestions {
      width: 100%;
    }

    :deep(.pickcode-auto-complete .ant-select-selector) {
      height: 42px !important;
    }

    :deep(.pickcode-auto-complete .ant-select-selection-search-input) {
      font-size: 16px !important;
      height: 38px !important;
      line-height: 38px !important;
    }

    :deep(.pickcode-auto-complete .ant-select-selection-placeholder) {
      font-size: 16px !important;
    }

    .verify-btn,
    .scan-open-btn {
      width: 100%;
      font-size: 16px;
    }

    .success-toast {
      width: 100%;
      box-sizing: border-box;
      justify-content: center;
    }

    .codes-container {
      margin-top: 6px;
    }

    .codes-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      margin-bottom: 12px;
    }

    .codes-title .label {
      font-size: 18px;
    }

    .codes-title .badge {
      min-width: 34px;
      height: 28px;
      font-size: 15px;
    }

    .last-refresh {
      font-size: 13px;
    }

    .codes-list {
      padding: 14px;
      border-radius: 14px;
    }

    .codes-list-transition {
      gap: 10px;
    }

    .code-item {
      width: 100%;
      min-width: 0;
      padding: 12px 14px;
      border-radius: 10px;
    }

    .code-text {
      font-size: 28px;
      letter-spacing: 2px;
      word-break: break-all;
    }

    .vip-icon {
      font-size: 20px;
    }

    .scan-modal {
      width: calc(100vw - 20px);
      padding: 12px;
    }

    .scan-view {
      height: 280px;
      min-height: 280px;
    }

    .scan-modal-title {
      font-size: 18px;
    }

    .shop-select-modal {
      min-width: auto;
      width: calc(100vw - 28px);
      padding: 20px;
    }
  }

  /* Popover 订单详情样式 */
  .order-popover-content {
    min-width: 320px;
    max-width: 400px;
    padding: 8px 0;
  }

  .popover-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  .popover-item:last-child {
    border-bottom: none;
  }

  .popover-label {
    color: rgba(0, 0, 0, 0.65);
    font-size: 14px;
    font-weight: 500;
    margin-right: 12px;
  }

  .popover-value {
    color: rgba(0, 0, 0, 0.85);
    font-size: 14px;
    text-align: right;
    flex: 1;
  }

  /* 菜品明细样式 */
  .popover-divider {
    height: 1px;
    background: rgba(0, 0, 0, 0.1);
    margin: 8px 0;
  }

  .popover-section-title {
    font-size: 15px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
    margin-bottom: 6px;
    padding: 2px 0;
  }

  .popover-food-list {
    margin-top: 6px;
  }

  .popover-food-list-scroll {
    max-height: 300px;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 4px;
  }

  .popover-food-list-scroll::-webkit-scrollbar {
    width: 6px;
  }

  .popover-food-list-scroll::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
  }

  .popover-food-list-scroll::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }

  .popover-food-list-scroll::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
  }

  .popover-food-item {
    padding: 4px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  }

  .popover-food-item:last-child {
    border-bottom: none;
  }

  .food-name-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2px;
  }

  .food-name {
    font-size: 14px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.85);
    flex: 1;
  }

  .food-quantity {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.65);
    margin-left: 8px;
  }

  .food-spec,
  .food-cook,
  .food-remark {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
    margin-top: 1px;
    line-height: 1.3;
  }

  .food-price-row {
    display: flex;
    justify-content: flex-end;
    margin-top: 2px;
  }

  .food-price {
    font-size: 14px;
    font-weight: 600;
    color: rgba(249, 115, 22, 0.9);
  }

  /* 店铺选择模态框样式 */
  .shop-select-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.75);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 50;
    backdrop-filter: blur(4px);
  }

  .shop-select-modal {
    background: linear-gradient(135deg, #252936 0%, #1a1d29 100%);
    border-radius: 16px;
    padding: 24px 28px;
    min-width: 360px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(59, 130, 246, 0.2);
    text-align: center;
    border: 1px solid rgba(59, 130, 246, 0.2);
  }

  .shop-select-title {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 16px;
    color: #ffffff;
  }

  .shop-select-body {
    margin-bottom: 20px;
  }

  .shop-select-dropdown {
    width: 100%;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid rgba(59, 130, 246, 0.3);
    background: rgba(37, 42, 58, 0.9);
    color: #ffffff;
    font-size: 16px;
    outline: none;
    transition: all 0.3s ease;
  }

  .shop-select-dropdown:hover {
    border-color: rgba(59, 130, 246, 0.5);
  }

  .shop-select-dropdown:focus {
    border-color: rgba(59, 130, 246, 0.8);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
  }

  .shop-select-dropdown option {
    background: #252936;
    color: #ffffff;
  }

  .shop-select-footer {
    display: flex;
    justify-content: center;
  }

  .shop-select-btn.confirm {
    padding: 8px 32px;
    border-radius: 999px;
    border: none;
    background: linear-gradient(135deg, #f97316, #ea580c);
    color: #ffffff;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    box-shadow: 0 4px 15px rgba(249, 115, 22, 0.4);
    transition: all 0.3s ease;
  }

  .shop-select-btn.confirm:hover {
    background: linear-gradient(135deg, #ea580c, #dc2626);
    box-shadow: 0 6px 20px rgba(249, 115, 22, 0.5);
    transform: translateY(-1px);
  }

  .scan-modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.75);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 60;
    backdrop-filter: blur(4px);
  }

  .scan-modal {
    width: min(560px, calc(100vw - 28px));
    background: linear-gradient(135deg, #252936 0%, #1a1d29 100%);
    border-radius: 16px;
    padding: 16px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(59, 130, 246, 0.2);
    border: 1px solid rgba(59, 130, 246, 0.25);
  }

  .scan-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  .scan-modal-title {
    margin: 0;
    font-size: 20px;
    color: #ffffff;
  }

  .scan-close-btn {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: none;
    background: rgba(255, 255, 255, 0.15);
    color: #ffffff;
    cursor: pointer;
  }

  .scan-modal-status {
    display: flex;
    justify-content: space-between;
    color: rgba(255, 255, 255, 0.75);
    font-size: 13px;
    margin-bottom: 10px;
  }

  .scan-feedback {
    margin-bottom: 10px;
    padding: 8px 10px;
    border-radius: 10px;
    background: rgba(15, 23, 42, 0.55);
    border: 1px solid rgba(59, 130, 246, 0.2);
  }

  .scan-feedback-line {
    margin: 0;
    color: rgba(255, 255, 255, 0.82);
    font-size: 13px;
    line-height: 1.5;
    word-break: break-all;
  }

  .scan-feedback-line.result {
    color: #93c5fd;
    margin-top: 2px;
  }

  .scan-panel {
    position: relative;
    border-radius: 14px;
    border: 1px solid rgba(59, 130, 246, 0.35);
    padding: 10px;
    background: rgba(13, 16, 26, 0.72);
  }

  .scan-view {
    width: 100%;
    height: 360px;
    min-height: 360px;
    border-radius: 10px;
    overflow: hidden;
  }

  .scan-empty {
    position: absolute;
    inset: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
    padding: 16px;
  }

  :deep(.scan-view > div) {
    border: none !important;
    height: 100% !important;
  }

  :deep(.scan-view video) {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
    border-radius: 10px;
  }
</style>
